import React, { createContext, useContext, useState, useEffect } from 'react';

export interface BrowserTab {
  id: string;
  url: string;
  title: string;
  favicon?: string;
  isLoading: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
}

interface BrowserContextType {
  tabs: BrowserTab[];
  activeTabId: string | null;
  addTab: (url: string, title?: string) => void;
  closeTab: (tabId: string) => void;
  switchTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<BrowserTab>) => void;
  addToHistory: (url: string, title: string) => void;
  history: Array<{ url: string; title: string; timestamp: number }>;
  clearHistory: () => void;
}

const BrowserContext = createContext<BrowserContextType | undefined>(undefined);

const HISTORY_KEY = 'vx-browser-history';
const MAX_HISTORY = 100;

export const BrowserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tabs, setTabs] = useState<BrowserTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<{ url: string; title: string; timestamp: number }>>([]);

  useEffect(() => {
    // 从 localStorage 读取历史记录
    const stored = localStorage.getItem(HISTORY_KEY);
    if (stored) {
      try {
        setHistory(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse browser history:', e);
      }
    }
  }, []);

  const addTab = (url: string, title: string = '新标签页') => {
    const newTab: BrowserTab = {
      id: `tab-${Date.now()}-${Math.random()}`,
      url,
      title,
      isLoading: true,
      canGoBack: false,
      canGoForward: false,
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
  };

  const closeTab = (tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);
      if (activeTabId === tabId && newTabs.length > 0) {
        setActiveTabId(newTabs[newTabs.length - 1].id);
      } else if (newTabs.length === 0) {
        setActiveTabId(null);
      }
      return newTabs;
    });
  };

  const switchTab = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const updateTab = (tabId: string, updates: Partial<BrowserTab>) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, ...updates } : tab
    ));
  };

  const addToHistory = (url: string, title: string) => {
    const newHistory = [
      { url, title, timestamp: Date.now() },
      ...history.filter(h => h.url !== url).slice(0, MAX_HISTORY - 1)
    ];
    setHistory(newHistory);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  return (
    <BrowserContext.Provider value={{
      tabs,
      activeTabId,
      addTab,
      closeTab,
      switchTab,
      updateTab,
      addToHistory,
      history,
      clearHistory,
    }}>
      {children}
    </BrowserContext.Provider>
  );
};

export const useBrowser = () => {
  const context = useContext(BrowserContext);
  if (!context) {
    throw new Error('useBrowser must be used within BrowserProvider');
  }
  return context;
};
